// Shared client-side validation for report create forms.
// Catches bad input before it reaches the API (which rejects, e.g., a
// non-YYYY-MM-DD date with a 400).

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** True for a real 'YYYY-MM-DD' calendar date (rejects e.g. 2026-13-40). */
export function isValidISODate(s: string): boolean {
  if (!ISO_DATE.test(s)) return false;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/**
 * Returns an error message for a date field, or null if ok.
 * Empty is allowed unless `required`.
 */
export function dateError(label: string, value: string, required = false): string | null {
  if (!value) return required ? `${label} is required` : null;
  if (!isValidISODate(value)) return `${label} must be a valid date (YYYY-MM-DD)`;
  return null;
}

/** Error if a report score is outside 1-100 (blank/undefined is allowed). */
export function scoreError(label: string, value: number | '' | undefined | null): string | null {
  if (value === '' || value === undefined || value === null || Number.isNaN(value)) return null;
  if (value < 1 || value > 100) return `${label} must be between 1 and 100`;
  return null;
}

/** Error if a stat is negative (blank/undefined is allowed). */
export function nonNegativeError(label: string, value: number | '' | undefined | null): string | null {
  if (value === '' || value === undefined || value === null || Number.isNaN(value)) return null;
  if (value < 0) return `${label} cannot be negative`;
  return null;
}

/** Returns the first non-null error from a list of checks, or null. */
export function firstError(...errors: (string | null)[]): string | null {
  return errors.find((e) => e !== null) ?? null;
}
